import React, { useState, useEffect, useRef } from 'react';
import { auth, provider } from './firebaseConfig';
import { signInWithPopup, onAuthStateChanged, signOut } from 'firebase/auth';
import './App.css';
import Table from './components/Table';
import Welcome from './components/Welcome';
import { TagInput } from './components/Table';

function App() {
    const [user, setUser] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const [searchTags, setSearchTags] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const tags = params.get('tags');
        return tags ? tags.split(',') : [];
    });
    const [allTags, setAllTags] = useState([]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            console.log("Auth state changed:", currentUser);
        });

        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            unsubscribe();
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleLogin = async () => {
        try {
            const result = await signInWithPopup(auth, provider);
            setUser(result.user);
        } catch (error) {
            console.error("Error during sign-in:", error);
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            setUser(null);
            setShowDropdown(false);
        } catch (error) {
            console.error("Error during sign-out:", error);
        }
    };

    const updateUrlParams = (tags) => {
        const params = new URLSearchParams(window.location.search);
        if (tags.length > 0) {
            params.set('tags', tags.join(','));
        } else {
            params.delete('tags');
        }
        
        window.history.pushState(
            {}, 
            '', 
            `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`
        );
    };

    const handleSearch = (newTag) => {
        if (!searchTags.includes(newTag)) {
            const newTags = [...searchTags, newTag];
            setSearchTags(newTags);
            updateUrlParams(newTags);
        }
    };

    const handleRemoveSearchTag = (tagToRemove) => {
        const newTags = searchTags.filter(tag => tag !== tagToRemove);
        setSearchTags(newTags);
        updateUrlParams(newTags);
    };

    const handleClearSearch = () => {
        setSearchTags([]);
        updateUrlParams([]);
    };

    const updateAllTags = (tags) => {
        setAllTags(tags);
    };

    useEffect(() => {
        const handlePopState = () => {
            const params = new URLSearchParams(window.location.search);
            const tags = params.get('tags');
            setSearchTags(tags ? tags.split(',') : []);
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    if (!user) {
        return <Welcome onLogin={handleLogin} />;
    }

    return (
        <div className="w-full">
            <header className="App-header flex justify-between items-center p-4 bg-indigo-50 shadow-md rounded-lg">
                <div className="flex-1 mx-4">
                    <TagInput
                        tags={searchTags}
                        onChange={(newTags) => {
                            setSearchTags(newTags);
                            updateUrlParams(newTags);
                        }}
                        existingTags={allTags}
                        placeholder="Search..."
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.target.value.trim() === '') {
                                e.preventDefault();
                            }
                        }}
                    />
                </div>
                <div className="ml-4 relative">
                    <div 
                        ref={dropdownRef}
                        className="relative cursor-pointer"
                        onClick={() => setShowDropdown(!showDropdown)}
                    >
                        <div className="flex items-center">
                            {user.photoURL && (
                                <img 
                                    src={user.photoURL} 
                                    alt="Profile" 
                                    className="w-8 h-8 rounded-full mr-2 ring-2 ring-indigo-200"
                                />
                            )}
                            <span className="text-indigo-900">{user.displayName}</span>
                        </div>

                        {showDropdown && (
                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
                                <button
                                    onClick={handleLogout}
                                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-50"
                                >
                                    <i className="fas fa-sign-out-alt mr-2"></i>
                                    Sign out
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <main className="p-4">
                <h1 className="text-2xl font-bold mb-4">Bookmarks</h1>
                <Table 
                    searchTags={searchTags} 
                    onSearch={handleSearch}
                    onTagsUpdate={updateAllTags}
                />
            </main>
        </div>
    );
}

export default App;
